import React from 'react';
import clsx from 'clsx';
import Collapsible from '@dashboard/components/Collapsible';
import { DragIcon } from '@dashboard/icons/DragIcon';
import Button, { getButtonClass } from '@dashboard/components/Button';
import Tooltip from '@dashboard/components/Tooltip';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cog8ToothIcon,
  EllipsisVerticalIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import { useTicketGroupHelpers } from '../useTicketGroupHelpers';
import TicketTypeEdit from './TicketTypeEdit';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import { Label } from '@dashboard/components/Form';
import TicketTypesList from './TicketTypesList';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { confirm } from '@dashboard/lib/confirm';
import TicketGroupLayoutForm from './TicketGroupLayoutForm';
import TicketGroupSettingsForm from './TicketGroupSettingsForm';
import usePage from '@dashboard/hooks/usePage';
import { useTicketGroups } from '../TicketGroupsContext';
import TicketGroupSaleDate from '@/dashboard/scenes/PageOverview/components/TicketGroupSaleDate';
import TicketGroupBadges from './TicketGroupBadges';

function checkCanDeleteTicketGroup(ticketGroup, page) {
  const hasSoldTicketTypes = ticketGroup.ticketTypes.some(
    ticketType => ticketType.quantitySold > 0,
  );

  if (hasSoldTicketTypes) {
    return {
      canDeleteTicketGroup: false,
      canDeleteTicketGroupMessage:
        "You can't delete this ticket group because it has made sales. You can archive it instead.",
    };
  }

  if (page.ticketGroups.length === 1) {
    return {
      canDeleteTicketGroup: false,
      canDeleteTicketGroupMessage: 'Add a new ticket group before deleting this one.',
    };
  }

  // if all ticket types on the page are in this ticket group, then it can't be deleted
  // meaning, no other ticket groups have tickets
  const ticketTypes = page.ticketGroups.flatMap(tg => tg.ticketTypes);
  const hasNoTicketTypesOutsideOfThisTicketGroup = ticketTypes.every(
    ticketType => ticketType.ticketGroup === ticketGroup._id,
  );

  if (hasNoTicketTypesOutsideOfThisTicketGroup) {
    return {
      canDeleteTicketGroup: false,
      canDeleteTicketGroupMessage:
        "You can't delete this ticket group because no other ticket groups have tickets. Add tickets to your other ticket groups to delete this one.",
    };
  }

  return {
    canDeleteTicketGroup: true,
    canDeleteTicketGroupMessage: '',
  };
}

export default function TicketGroupListItem({
  ticketGroup,
  dragHandleProps,
  isNewPageFlow = false,
}) {
  const { page } = usePage();
  const {
    setIsEditingTicketGroup,
    isAddingTicketType,
    isEditingTicketType,
    editingTicketGroupId,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isEditingTicketGroupSettings,
    setIsEditingTicketGroupSettings,
    isEditingTicketGroupLayout,
    setIsEditingTicketGroupLayout,
  } = useTicketGroupEdit();

  const { state: ticketGroups } = useTicketGroups();

  const {
    onEnableDisableTicketGroup,
    onDuplicateTicketGroup,
    onDeleteTicketGroup,
    totalQuantitySoldInGroup,
  } = useTicketGroupHelpers(ticketGroup._id);

  const { onAddTicketType } = useTicketTypeHelpers();

  const enabledTicketTypesCount = ticketGroup.ticketTypes?.filter(
    ticketTypes => ticketTypes.status === 'enabled',
  ).length;

  const { canDeleteTicketGroup, canDeleteTicketGroupMessage } = checkCanDeleteTicketGroup(
    ticketGroup,
    page,
  );

  const isEditing = editingTicketGroupId === ticketGroup._id;

  return (
    <div className="w-full">
      <Collapsible.Root
        open={isEditing}
        onOpenChange={open => {
          setIsEditingTicketGroup(open, ticketGroup._id);
        }}
      >
        <div
          className={clsx(
            'flex flex-row gap-2 items-center justify-start sticky top-0 z-10 rounded-t bg-white',
          )}
        >
          <div {...dragHandleProps}>
            <DragIcon className="w-4 h-4" />
          </div>

          <Collapsible.Trigger
            as="button"
            className={clsx(
              `py-3 truncate w-full text-left flex items-center justify-between !ring-0 !focus:ring-0`,
              getButtonClass('bare'),
              {
                '!text-gray-900 !hover:text-gray-900 cursor-not-allowed': isAddingTicketType, // remove hover effect when adding ticket type
                'opacity-50 pointer-events-none': ticketGroup.status === 'archived',
              },
            )}
            type="button"
          >
            <>
              <div className="flex flex-col gap-y-0.5 flex-1 min-w-0">
                <span
                  className={clsx('font-medium truncate overflow-hidden mr-2', {
                    'opacity-50': ticketGroup.status === 'disabled',
                  })}
                >
                  {ticketGroup.name}
                </span>

                <TicketGroupBadges
                  page={page}
                  ticketGroup={ticketGroup}
                  allTicketGroups={ticketGroups}
                />
                {ticketGroup.availabilityStatus === 'available' &&
                  ticketGroup.status === 'enabled' && (
                    <TicketGroupSaleDate
                      page={page}
                      ticketGroup={ticketGroup}
                      allTicketGroups={ticketGroups || []}
                    />
                  )}
                <div className="text-sm text-gray-700">
                  {ticketGroup?.capacity !== null ? `${ticketGroup.capacity} tickets` : 'Unlimited'}
                  {' • '}
                  {totalQuantitySoldInGroup || '0'} sold
                </div>
              </div>
            </>

            <div className="flex gap-x-2 items-center flex-shrink-0">
              <>
                <span className="text-xs text-gray-500">
                  {enabledTicketTypesCount} ticket
                  {enabledTicketTypesCount === 1 ? '' : 's'}
                </span>
              </>

              <span className="ml-auto">
                {isEditing ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </span>
            </div>
          </Collapsible.Trigger>

          <div className="flex ml-auto gap-2 items-center">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                asChild
                disabled={isAddingTicketType || ticketGroup.status === 'archived'}
              >
                <Button
                  aria-label="more options"
                  appearance="icon-button"
                  size="narrow"
                  disabled={isAddingTicketType || ticketGroup.status === 'archived'}
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="origin-top-right absolute right-0 z-20 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-gray-100 focus:outline-none overflow-hidden"
                  side="bottom"
                  align="end"
                >
                  <DropdownMenu.Item
                    onClick={onDuplicateTicketGroup}
                    className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                  >
                    Duplicate
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={onEnableDisableTicketGroup}
                    className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                  >
                    {ticketGroup.status === 'enabled' ? 'Disable' : 'Enable'}
                  </DropdownMenu.Item>

                  {canDeleteTicketGroup ? (
                    <DropdownMenu.Item
                      onClick={onDeleteTicketGroup}
                      className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                    >
                      Delete
                    </DropdownMenu.Item>
                  ) : (
                    <RadixTooltip.Provider delayDuration={300}>
                      <RadixTooltip.Root>
                        <DropdownMenu.Item asChild disabled>
                          <RadixTooltip.Trigger className="w-full">
                            <button className="text-left w-full p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900 opacity-50 pointer-events-none">
                              Delete
                            </button>
                          </RadixTooltip.Trigger>
                        </DropdownMenu.Item>

                        <RadixTooltip.Content
                          className={clsx(
                            'data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none leading-none will-change-[transform,opacity]',
                            'inline-flex items-center rounded px-1.5 py-1.5 mx-2',
                            'bg-black dark:bg-gray-800 shadow z-50',
                          )}
                          side="left"
                        >
                          <RadixTooltip.Arrow className="fill-current text-black dark:text-gray-800 mx-auto" />
                          <span className="block text-sm text-white dark:text-gray-100 max-w-xs text-left">
                            {canDeleteTicketGroupMessage}
                          </span>
                        </RadixTooltip.Content>
                      </RadixTooltip.Root>
                    </RadixTooltip.Provider>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>

        <Collapsible.Content
          className={clsx(`pb-4 pt-2 relative`, {
            'opacity-50 pointer-events-none': ticketGroup.status === 'disabled',
          })}
        >
          <div className="flex flex-col">
            {ticketGroup.ticketTypes.length > 0 && (
              <div className="flex flex-col gap-y-2">
                <TicketTypesList
                  ticketTypes={ticketGroup.ticketTypes}
                  ticketGroupId={ticketGroup._id}
                />
              </div>
            )}

            {isAddingTicketType && (
              <div
                className={clsx('mt-4 pl-6 pr-8', {
                  '!mt-0': ticketGroup.ticketTypes.length === 0,
                })}
              >
                <Label>New ticket</Label>
                <TicketTypeEdit ticketGroupId={ticketGroup._id} />
              </div>
            )}

            {!isAddingTicketType && !isEditingTicketType && (
              <>
                <div className="mt-4 flex gap-x-2 ml-6 mr-[26px] justify-between">
                  <div className="flex gap-x-2">
                    <Tooltip label="Group settings">
                      <Button
                        handleClick={async () => {
                          let proceed = true;

                          if (hasUnsavedChanges) {
                            proceed = await confirm('Discard unsaved changes?', {
                              confirm: 'Discard',
                            });
                          }

                          if (!proceed) return;

                          setIsEditingTicketGroupSettings(!isEditingTicketGroupSettings);
                          setIsEditingTicketGroupLayout(false);
                          setHasUnsavedChanges(false);
                        }}
                        appearance="secondary"
                        className={clsx({
                          '!border-chocolate-1000': isEditingTicketGroupSettings,
                        })}
                      >
                        <Cog8ToothIcon className="w-5 h-5" />
                      </Button>
                    </Tooltip>

                    {!isNewPageFlow && (
                      <Tooltip label="Group layout">
                        <Button
                          handleClick={async () => {
                            let proceed = true;

                            if (hasUnsavedChanges) {
                              proceed = await confirm('Discard unsaved changes?', {
                                confirm: 'Discard',
                              });
                            }

                            if (!proceed) return;

                            setIsEditingTicketGroupLayout(!isEditingTicketGroupLayout);
                            setIsEditingTicketGroupSettings(false);
                            setHasUnsavedChanges(false);
                          }}
                          appearance="secondary"
                          className={clsx({
                            '!border-chocolate-1000': isEditingTicketGroupLayout,
                          })}
                        >
                          <Squares2X2Icon className="w-5 h-5" />
                        </Button>
                      </Tooltip>
                    )}
                  </div>

                  <Button
                    appearance={isNewPageFlow ? 'secondary' : 'primary'}
                    onClick={() => onAddTicketType(ticketGroup._id)}
                  >
                    + Add ticket
                  </Button>
                </div>
                {(isEditingTicketGroupLayout || isEditingTicketGroupSettings) && (
                  <div className="ml-6 mr-[26px] mt-4">
                    {isEditingTicketGroupLayout && (
                      <TicketGroupLayoutForm
                        ticketGroup={ticketGroup}
                        onClose={() => {
                          setIsEditingTicketGroupLayout(false);
                          setHasUnsavedChanges(false);
                        }}
                      />
                    )}

                    {isEditingTicketGroupSettings && (
                      <TicketGroupSettingsForm
                        ticketGroup={ticketGroup}
                        onClose={() => {
                          setIsEditingTicketGroupSettings(false);
                          setHasUnsavedChanges(false);
                        }}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}
